<template>
  <div>
    <el-form
      v-if="flatDeal"
      ref="form"
      :model="form"
      :label-position="isDesktop ? 'left' : 'top'"
      :class="{ desktop: isDesktop }"
      label-width="200px"
      @submit.prevent
    >
      <el-form-item label="Stream">
        <StreamSelector
          v-model="flatDeal.stream"
          :allowed-streams="[flatDeal.stream]"
        />
      </el-form-item>
      <el-form-item label="Ad type">
        <AdTypeSelect
          v-model="flatDeal.type_id"
          :allowed-types="[flatDeal.type_id]"
          :lables="FlatDealAdTypeLabels"
          :radio-view="true"
        />
      </el-form-item>
      <el-form-item label="Spots filter">
        <RonTypeSelector v-model="flatDeal.ron_type" :disabled="true" />
      </el-form-item>
      <el-form-item v-if="flatDeal.spot_ids.length" label="Spots">
        <SpotsSelector
          v-model="flatDeal.spot_ids"
          :stream="flatDeal.stream"
          :type-id="flatDeal.type_id"
          :disabled="true"
        />
      </el-form-item>
      <el-form-item v-if="flatDeal.type_id === AdType.Banner" label="Size">
        <SizeSelect v-model="flatDeal.size_id" :disabled="true" />
      </el-form-item>
      <el-form-item :class="{ desktop: isDesktop }" label-position="top">
        <CountriesBlock v-model="flatDeal.country_codes" />
      </el-form-item>
      <StatsInfo :id="id" />
      <DeviceSelector
        v-model="flatDeal.device_ids"
        label-width=""
        :disabled="true"
      />
      <el-form-item label="Referrer hosts filter">
        <WhiteBlackLists
          v-model="flatDeal.use_whitelist_domains"
          :disabled="true"
          title="Domains"
          type="domains"
          hint="Allow/Disallow traffic from listed hosts. One host per line without https and www."
          :black-list="flatDeal.blacklist_domains"
          :white-list="flatDeal.whitelist_domains"
        />
      </el-form-item>
      <el-form-item>
        <el-descriptions title="Deal price" :column="1" border>
          <el-descriptions-item v-if="flatDeal.logo" label="Logo">
            <img :src="flatDeal.logo" class="logo" alt="logo" />
          </el-descriptions-item>
          <el-descriptions-item label="Period">
            <PeriodLabel :from="flatDeal.start" :to="flatDeal.stop" />
          </el-descriptions-item>
          <el-descriptions-item :label="rateLabel">
            {{ ratePrice }}$
          </el-descriptions-item>
          <el-descriptions-item v-if="flatDeal.daily_limit" label="Daily limit">
            {{ flatDeal.daily_limit }}
          </el-descriptions-item>
          <el-descriptions-item label="Total Deal Price">
            {{ totalPrice }}
          </el-descriptions-item>
        </el-descriptions>
      </el-form-item>
      <el-alert
        v-if="flatDeal.traffic_delivery_warning"
        class="traffic-delivery-warning"
        type="warning"
        effect="light"
        :closable="false"
        show-icon
      >
        {{ flatDeal.traffic_delivery_warning }}
      </el-alert>
      <el-tooltip
        v-if="!disabled"
        :disabled="!notEnoughMoney"
        effect="dark"
        placement="top-start"
        content="Not enough money to start this deal"
      >
        <el-form-item>
          <router-link
            :is="notEnoughMoney ? 'button' : 'router-link'"
            :disabled="notEnoughMoney"
            :to="`/direct_deals/${id}/buy`"
            class="el-button el-button--success is-plain"
          >
            <span>Buy</span>
          </router-link>
          <router-link to="/direct_deals" class="el-button is-plain">
            <span>Cancel</span>
          </router-link>
        </el-form-item>
      </el-tooltip>
    </el-form>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { FlatDealAdTypeLabels } from '@/apps/traforama/lables'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelector from '@/apps/traforama/components/StreamSelector.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import CountriesBlock from '@/apps/traforama/components/CountriesBlock.vue'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import RonTypeSelector from '@/apps/traforama/views/campaigns/components/RonTypeSelector.vue'
import WhiteBlackLists from '@/apps/traforama/components/WhiteBlackLists.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import PeriodLabel from './PeriodLabel.vue'
import StatsInfo from './StatsInfo.vue'

import { useUserStore } from '@/store/modules/user'

import useResize from '@/hooks/useResize'
import { AdType, FlatDealType } from '@/api/types'

export default defineComponent({
  name: 'FlatDealInfo',
  components: {
    AdTypeSelect,
    StreamSelector,
    WhiteBlackLists,
    DeviceSelector,
    StatsInfo,
    CountriesBlock,
    SpotsSelector,
    PeriodLabel,
    RonTypeSelector,
    SizeSelect
  },
  props: {
    modelValue: {
      required: true,
      type: Object
    },
    id: {
      required: true,
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, _) {
    const userStore = useUserStore()
    const form = ref({})
    const { isDesktop } = useResize()

    const rateLabel = computed(() => {
      return FlatDealType.DailyFee === props.modelValue.deal_type
        ? 'Daily Rate'
        : 'Fix CPM'
    })

    const ratePrice = computed(() => {
      return FlatDealType.DailyFee === props.modelValue.deal_type
        ? props.modelValue.daily_rate
        : props.modelValue.flat_rate
    })

    const notEnoughMoney = computed(() => {
      if (!props.modelValue.total_price) return false

      return userStore.balance / 100 < props.modelValue.total_price
    })

    const totalPrice = computed(() => {
      if (props.modelValue.total_price)
        return `${props.modelValue.total_price}$`
      if (props.modelValue.daily_limit) {
        const price = (
          (props.modelValue.total_days *
            props.modelValue.flat_rate *
            props.modelValue.daily_limit) /
          1000
        ).toFixed(2)
        return `${price}$`
      }
      return 'Depends on daily limit'
    })

    return {
      FlatDealAdTypeLabels,
      form,
      AdType,
      isDesktop,
      totalPrice,
      notEnoughMoney,
      rateLabel,
      ratePrice,

      flatDeal: props.modelValue
    }
  }
})
</script>

<style lang="scss" scoped>
.logo {
  max-width: -webkit-fill-available;
  max-height: 100px;
}

:deep(.el-input.is-disabled .el-input__inner) {
  color: black;
}

:deep(.el-checkbox__input.is-disabled + span.el-checkbox__label) {
  color: black;
}

:deep(.el-radio__input.is-disabled + span.el-radio__label) {
  color: black;
}

.body {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.el-descriptions {
  width: 100%;
}

.el-form {
  margin: 0 20px;
}

.traffic-delivery-warning {
  margin: 8px 0 12px 200px;
}

@media (max-width: 767px) {
  .traffic-delivery-warning {
    margin-left: 0;
  }
}
</style>
